import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import EditPredefinedTask from './EditPredefinedTask';
import DeleteIcon from '@mui/icons-material/Delete';
import { useToast } from '../../providers/ToastProvider';
import { Session } from '../../peer-colab-model/Session';
import { PredefinedTaskManagement } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import { PredefinedTask } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Tasks/PathItems';
import AISuggestions from '../../components/AISuggestions';

function PredefinedTasks() {
  const { onError } = useToast();
  const [search, setSearch] = useState('');
  const [filteredTasks, setFilteredTasks] = useState<PredefinedTask[]>([]);
  const [editingTask, setEditingTask] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskType, setNewTaskType] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<{ id: string, name: string } | null>(null);

  const searchTasks = async (query: string) => {
    onError((await Session.getClient().request(PredefinedTaskManagement.searchPredefinedTask({ query, limit: 100, offset: 0 })))
      .maybeOk((value) => setFilteredTasks(value as PredefinedTask[])));
  };

  useEffect(() => {
    searchTasks('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddTask = () => {
    setNewTaskName('');
    setNewTaskType('');
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewTaskName('');
    setNewTaskType('');
  };

  const handleDialogOk = async () => {
    if (newTaskName.trim()) {
      onError((await Session.getClient().request(PredefinedTaskManagement.createPredefinedTask({ name: newTaskName.trim(), type: newTaskType.trim() })))
        .maybeOk(() => searchTasks(search)));
    }
    handleDialogClose();
  };

  const handleDeleteClick = (id: string, name: string) => {
    setTaskToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setTaskToDelete(null);
  };

  const handleDeleteDialogOk = async () => {
    if (taskToDelete) {
      onError((await Session.getClient().addPathParam('predefinedTaskId', taskToDelete.id).request(PredefinedTaskManagement.deletePredefinedTask()))
        .maybeOk(() => searchTasks(search)));
    }
    handleDeleteDialogClose();
  };

  if (editingTask) {
    return <EditPredefinedTask id={editingTask} onBack={() => {
      searchTasks(search);
      setEditingTask(null);
    }} />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <TextField
          label="Search predefined tasks"
          size="small"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            searchTasks(e.target.value);
          }}
          sx={{ width: 250 }}
        />
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <AISuggestions 
            client={Session.getClient()}
            currentSnapshot={JSON.stringify(filteredTasks)}
            usageInstructions={PredefinedTaskManagement.toAgentInstructions()}
            developerMessage={'The user is in the view where they can search and see a list of predefined tasks. For any of the operations ' +
              'regarding a specific task the path parameter with the name predefinedTaskId MUST be specified with that task id of that task.'}
            onOperationsApplied={() => searchTasks(search)}
          />
        </Box>
        <Button variant="contained" color="primary" onClick={handleAddTask}>Create Predefined Task</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><Typography fontWeight="bold">Name</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Type</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight="bold">Actions</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTasks.map((task) => (
              <TableRow key={task.id}>
                <TableCell>{task.name}</TableCell>
                <TableCell>{task.type}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="primary" onClick={() => setEditingTask(task.id)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteClick(task.id, task.name)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Create Predefined Task</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Task Name"
            fullWidth
            autoFocus
            value={newTaskName}
            onChange={e => setNewTaskName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Type"
            fullWidth
            select
            value={newTaskType}
            onChange={e => setNewTaskType(e.target.value)}
            sx={{ mt: 2 }}
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="health">Health</MenuItem>
            <MenuItem value="longterm">Longterm</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogOk} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
        <DialogTitle>Delete Predefined Task</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the predefined task "{taskToDelete?.name}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteDialogOk} variant="contained" color="error">OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PredefinedTasks;
